import { Stack } from '@/components/layout';
import { Text } from '@/components/ui/text';
import { createStyles, useScaling } from '@/hooks';
import { useBlurProps } from '@/hooks/use-blur-props';
import { colors, guardianColors, withOpacity } from '@/theme/colors';
import { Device } from '@/theme/scaling';
import { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import {
  createRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  LayoutChangeEvent,
  Platform,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const blurTargetRefHome = createRef<View>();
export const blurTargetRefPlayer = createRef<View>();
export const blurTargetRefPlayerPathway = createRef<View>();

const TAB_ICON_SIZE = 24;
const GUARDIAN_TAB_ICON_SIZE = 32;
// Guardian tablet design: 708 / 1024 ~= 0.70.
const GUARDIAN_TAB_BAR_WIDTH = '70%';
const GUARDIAN_TAB_BAR_SHADOW_COLOR = '#0F225B';
/** Design tokens — keep in sync with tab bar `height` / `paddingY` below. */
export const TAB_BAR_HEIGHT_TOKEN = 56;
export const TAB_BAR_CONTAINER_TOP_PADDING_TOKEN = 8;
export const FLOATING_TAB_BAR_CLEARANCE_TOKEN =
  TAB_BAR_HEIGHT_TOKEN + TAB_BAR_CONTAINER_TOP_PADDING_TOKEN + 16;
const TAB_BUTTON_BORDER_RADIUS = 80;
const TAB_BAR_ROW_PADDING_Y_TOKEN = 4;

const SPRING_CONFIG = { damping: 30, stiffness: 300, mass: 0.8 };

interface TabLayout {
  x: number;
  y: number;
  width: number;
  height: number;
}

type TabbarDescriptor = BottomTabBarProps['descriptors'][string];
type TabBarIconFactory = NonNullable<TabbarDescriptor['options']['tabBarIcon']>;
type TabBarLabel = TabbarDescriptor['options']['tabBarLabel'];
type TabbarVariant = 'default' | 'guardian';

export type TabbarProps = BottomTabBarProps & {
  /** Android (expo-blur): ref to `BlurTargetView` wrapping tab screens — see Expo BlurView docs. */
  blurTargetRef?: RefObject<View | null>;
  variant?: TabbarVariant;
};

function useTabIndicator(activeIndex: number) {
  const { scale } = useScaling();
  /** Must match tab bar row `paddingY` + `height` so the pill fills the bar, not the tab content box (web centers row items and under-measures height). */
  const indicatorInsetY = scale(TAB_BAR_ROW_PADDING_Y_TOKEN);
  const indicatorVerticalSpan = scale(TAB_BAR_HEIGHT_TOKEN) - indicatorInsetY * 2;

  const tabLayouts = useRef<Record<number, TabLayout>>({});
  const hasInitialized = useRef(false);
  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;

  const indicatorLeft = useSharedValue(0);
  const indicatorWidth = useSharedValue(0);

  const indicatorStyle = useAnimatedStyle(
    () => ({
      left: indicatorLeft.value,
      top: indicatorInsetY,
      width: indicatorWidth.value,
      height: indicatorVerticalSpan,
    }),
    [indicatorInsetY, indicatorVerticalSpan],
  );

  // Stable across renders so `TabbarItem`'s `onLayout` prop reference doesn't change.
  // Reads `activeIndex` from a ref to keep this callback identity stable across tab switches.
  const handleItemLayout = useCallback(
    (index: number, event: LayoutChangeEvent) => {
      const { x, y, width, height } = event.nativeEvent.layout;
      tabLayouts.current[index] = { x, y, width, height };

      if (index === activeIndexRef.current) {
        if (!hasInitialized.current) {
          hasInitialized.current = true;
          indicatorLeft.value = x;
          indicatorWidth.value = width;
        } else {
          indicatorLeft.value = withSpring(x, SPRING_CONFIG);
          indicatorWidth.value = withSpring(width, SPRING_CONFIG);
        }
      }
    },
    [indicatorLeft, indicatorWidth],
  );

  const moveToIndex = useCallback(
    (index: number) => {
      const layout = tabLayouts.current[index];
      if (!layout) return;
      indicatorLeft.value = withSpring(layout.x, SPRING_CONFIG);
      indicatorWidth.value = withSpring(layout.width, SPRING_CONFIG);
    },
    [indicatorLeft, indicatorWidth],
  );

  return { indicatorStyle, handleItemLayout, moveToIndex };
}

const TabIndicator = memo(({ style }: { style: StyleProp<ViewStyle> }) => {
  const styles = useStyles();
  return <Animated.View style={[styles.indicator, style]} />;
});

type TabbarItemProps = {
  /** Stable index of the tab — passed back through callbacks so handlers stay reference-stable. */
  index: number;
  /** Route key — passed back through callbacks for the same reason. */
  routeKey: string;
  isFocused: boolean;
  color: string;
  /** Pre-resolved label (string from `tabBarLabel`/`title`/`route.name`, or a render function). */
  rawLabel: TabBarLabel | string;
  /** Fallback children for the function-form label. */
  labelChildren: string;
  iconFactory?: TabBarIconFactory;
  variant: TabbarVariant;
  accessibilityLabel?: string;
  testID?: string;
  onLayout: (index: number, event: LayoutChangeEvent) => void;
  onPress: (index: number, routeKey: string) => void;
  onLongPress: (routeKey: string) => void;
};

const TabbarItem = memo(
  ({
    index,
    routeKey,
    isFocused,
    color,
    rawLabel,
    labelChildren,
    iconFactory,
    variant,
    accessibilityLabel,
    testID,
    onLayout,
    onPress,
    onLongPress,
  }: TabbarItemProps) => {
    const { isTablet, scale } = useScaling();
    const styles = useStyles();
    const iconSize = variant === 'guardian' ? GUARDIAN_TAB_ICON_SIZE : TAB_ICON_SIZE;

    const handleLayout = useCallback(
      (event: LayoutChangeEvent) => onLayout(index, event),
      [onLayout, index],
    );
    const handlePress = useCallback(() => onPress(index, routeKey), [onPress, index, routeKey]);
    const handleLongPress = useCallback(() => onLongPress(routeKey), [onLongPress, routeKey]);

    const icon =
      iconFactory != null
        ? iconFactory({
            focused: isFocused,
            color,
            size: scale(iconSize),
          })
        : null;

    const labelNode =
      typeof rawLabel === 'function'
        ? (rawLabel as (props: any) => ReactNode)({
            focused: isFocused,
            color,
            position: 'below-icon',
            children: labelChildren,
          })
        : (rawLabel as ReactNode);

    const textVariant = () => {
      if (variant === 'guardian') {
        return 'Font Guardian App/Subtitle/Subtitle 14 Semibold';
      }
      if (isTablet) {
        return 'Font Inter/Subtitle/Medium 14px';
      }
      return 'Font Inter/Subtitle/Medium 8px';
    };

    return (
      <Stack flex={1} onLayout={handleLayout}>
        <TouchableOpacity
          accessibilityState={isFocused ? { selected: true } : {}}
          accessibilityLabel={accessibilityLabel}
          accessibilityRole="button"
          testID={testID}
          onPress={handlePress}
          activeOpacity={1}
          onLongPress={handleLongPress}
          style={styles.tabButton}>
          <Stack
            direction={isTablet ? 'row' : 'column'}
            align="center"
            justify="center"
            height={'100%'}
            borderRadius={TAB_BUTTON_BORDER_RADIUS}
            paddingY={scale(4)}
            paddingX={scale(4.5)}
            gap={isTablet ? scale(8) : scale(2)}>
            {icon}
            {typeof rawLabel === 'function' ? (
              labelNode
            ) : (
              <Text
                variant={textVariant()}
                numberOfLines={1}
                transform={variant === 'guardian' ? 'none' : 'uppercase'}
                style={{ color }}>
                {labelNode}
              </Text>
            )}
          </Stack>
        </TouchableOpacity>
      </Stack>
    );
  },
);

export const Tabbar = memo(
  ({ blurTargetRef, variant = 'default', ...tabBarProps }: TabbarProps) => {
    const { state, descriptors, navigation } = tabBarProps;
    const { bottom } = useSafeAreaInsets();
    const { indicatorStyle, handleItemLayout, moveToIndex } = useTabIndicator(state.index);
    const { blurAmount } = useBlurProps({ blurAmount: 15 });
    const { scale } = useScaling();
    const styles = useStyles();

    const useBlur = true;
    const tabBarWidth = variant === 'guardian' ? GUARDIAN_TAB_BAR_WIDTH : '100%';
    const androidBoxShadowStyle =
      variant === 'guardian' ? styles.guardianAndroidBoxShadow : styles.androidBoxShadow;
    const pillShadowWrapStyle =
      variant === 'guardian'
        ? styles.guardianPillShadowWrap
        : Platform.OS === 'web'
          ? styles.pillShadowWrapWeb
          : styles.pillShadowWrapIos;

    // Latest state/navigation kept in refs so the press/long-press handlers below remain
    // referentially stable across renders. Stable handlers let `TabbarItem`'s `memo()` skip
    // re-renders for tabs whose own props (`isFocused`, `color`) didn't change.
    const stateRef = useRef(state);
    stateRef.current = state;
    const navigationRef = useRef(navigation);
    navigationRef.current = navigation;

    const handleTabPress = useCallback(
      (index: number, routeKey: string) => {
        const event = navigationRef.current.emit({
          type: 'tabPress',
          target: routeKey,
          canPreventDefault: true,
        });
        const isFocused = stateRef.current.index === index;
        const route = stateRef.current.routes[index];
        if (!isFocused && !event.defaultPrevented && route) {
          moveToIndex(index);
          navigationRef.current.navigate(route.name, route.params);
        }
      },
      [moveToIndex],
    );

    const handleTabLongPress = useCallback((routeKey: string) => {
      navigationRef.current.emit({
        type: 'tabLongPress',
        target: routeKey,
      });
    }, []);

    useEffect(() => {
      moveToIndex(state.index);
    }, [moveToIndex, state.index]);

    const tabPill = useMemo(
      () => (
        <Stack
          viewType={useBlur ? 'liquid-glass' : 'view'}
          width="100%"
          intensity={blurAmount}
          tint="prominent"
          blurTarget={blurTargetRef}
          blurMethod={
            Platform.OS === 'android' && blurTargetRef ? 'dimezisBlurViewSdk31Plus' : undefined
          }
          overlayColor={withOpacity('#ffffff', 0.7)}
          minWidth={scale(339)}
          height={scale(TAB_BAR_HEIGHT_TOKEN)}
          borderRadius={scale(28)}
          direction="row"
          align="stretch"
          justify="space-around"
          backgroundColor={useBlur ? undefined : 'white'}
          paddingX={scale(4)}
          paddingY={scale(TAB_BAR_ROW_PADDING_Y_TOKEN)}
          style={Platform.OS === 'android' ? androidBoxShadowStyle : undefined}
          effect="regular">
          <TabIndicator style={indicatorStyle} />

          {state.routes.map((route, index) => {
            const isFocused = state.index === index;
            const color = isFocused
              ? 'white'
              : variant === 'guardian'
                ? guardianColors['6']
                : colors.Blue;
            const { options } = descriptors[route.key];
            const rawLabel =
              options.tabBarLabel !== undefined
                ? options.tabBarLabel
                : options.title !== undefined
                  ? options.title
                  : route.name;

            return (
              <TabbarItem
                key={route.key}
                index={index}
                routeKey={route.key}
                isFocused={isFocused}
                color={color}
                rawLabel={rawLabel}
                labelChildren={options.title ?? route.name}
                iconFactory={options.tabBarIcon}
                variant={variant}
                accessibilityLabel={options.tabBarAccessibilityLabel}
                testID={options.tabBarButtonTestID}
                onLayout={handleItemLayout}
                onPress={handleTabPress}
                onLongPress={handleTabLongPress}
              />
            );
          })}
        </Stack>
      ),
      [
        blurAmount,
        blurTargetRef,
        descriptors,
        handleItemLayout,
        handleTabLongPress,
        handleTabPress,
        indicatorStyle,
        scale,
        state.index,
        state.routes,
        androidBoxShadowStyle,
        useBlur,
        variant,
      ],
    );

    return (
      <Stack
        position="absolute"
        zIndex={1000}
        bottom={Platform.OS === 'web' ? 16 : 0}
        left={0}
        right={0}>
        <Stack
          viewType="view"
          justify="center"
          align="center"
          paddingTop={scale(TAB_BAR_CONTAINER_TOP_PADDING_TOKEN)}
          paddingBottom={bottom + (Platform.OS === 'android' ? Device.bottomHeight : 0)}
          paddingX={scale(16)}>
          <Stack width={tabBarWidth}>
            {Platform.OS === 'android' ? (
              tabPill
            ) : (
              <View style={pillShadowWrapStyle}>{tabPill}</View>
            )}
          </Stack>
        </Stack>
      </Stack>
    );
  },
);

const useStyles = createStyles(({ scale }) => ({
  tabButton: {
    flex: 1,
    borderRadius: scale(22),
    overflow: 'hidden',
  },
  indicator: {
    position: 'absolute',
    left: 0,
    top: 0,
    backgroundColor: colors.Blue,
    borderRadius: TAB_BUTTON_BORDER_RADIUS,
  },
  /**
   * iOS 17–18: `shadow*` on `LiquidGlassView` / blur often does not composite. Apply shadow on a
   * plain `View` wrapper with matching radius + a near-transparent fill so Core Animation draws a
   * shadow path (see Apple RN issues on material + shadow).
   */
  pillShadowWrapIos: {
    width: '100%',
    borderRadius: scale(28),
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.22,
    shadowRadius: 6,
  },
  /** Web: `react-native-web` maps `shadow*` → `box-shadow`; slightly softer/larger so it reads on screen. */
  pillShadowWrapWeb: {
    width: '100%',
    borderRadius: scale(28),
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
  },
  androidBoxShadow: {
    elevation: 2,
  },
  // Guardian design shadow: x 0, y 2, blur 8, spread 0, #0F225B at 12%.
  guardianPillShadowWrap: {
    width: '100%',
    borderRadius: scale(28),
    backgroundColor: colors[0],
    shadowColor: GUARDIAN_TAB_BAR_SHADOW_COLOR,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
  },
  guardianAndroidBoxShadow: {
    elevation: 3,
    shadowColor: GUARDIAN_TAB_BAR_SHADOW_COLOR,
  },
}));
